import { Fragment, useState } from 'react'
import { Link } from '@inertiajs/react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { auditLogBadgeClass, auditLogLabel } from '@/frontend/lib/auditLogs'

interface AuditLogRow {
  id: string
  audit_action: string
  description: string
  details: Record<string, unknown>
  ip_address: string | null
  created_at: string
  actor_name: string | null
  worker_name: string | null
  digital_worker_id: string | null
  organization_id: string
  organization_name: string
  worker_path: string | null
}

interface Pagination {
  page: number
  total_pages: number
  total: number
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface Props {
  audit_logs: AuditLogRow[]
  pagination: Pagination
  onPageChange: (page: number) => void
}

export default function AuditLogTable({ audit_logs, pagination, onPageChange }: Props) {
  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(new Set())

  const toggleExpanded = (logId: string) => {
    setExpandedLogIds((previous) => {
      const next = new Set(previous)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-auto max-h-[calc(100vh-240px)]">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted border-b border-border">
              <th className="w-12 px-4 py-3">
                <span className="sr-only">Expandir descrição</span>
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Quando</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Organização</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ação</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Descrição</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Colaborador</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ator</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {audit_logs.map((log) => {
              const isExpanded = expandedLogIds.has(log.id)
              const detailRowId = `audit-log-description-${log.id}`

              return (
                <Fragment key={log.id}>
                  <tr className="hover:bg-accent transition-colors">
                    <td className="px-4 py-3 align-top">
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={detailRowId}
                        aria-label={isExpanded ? 'Recolher descrição' : 'Expandir descrição'}
                        onClick={() => toggleExpanded(log.id)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{log.organization_name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${auditLogBadgeClass(log.audit_action)}`}>
                        {auditLogLabel(log.audit_action)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      <p className="max-w-xs truncate">{log.description}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {log.worker_path ? <Link href={log.worker_path} className="hover:underline">{log.worker_name}</Link> : (log.worker_name || '—')}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{log.actor_name || 'Sistema'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{log.ip_address || '—'}</td>
                  </tr>
                  {isExpanded && (
                    <tr id={detailRowId} className="bg-muted">
                      <td colSpan={8} className="px-4 pb-4 pt-0">
                        <div className="ml-11 rounded-lg border border-border bg-card px-4 py-3">
                          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Descrição completa</p>
                          <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">
                            {log.description}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {audit_logs.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nenhum evento de auditoria encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
          <span className="text-sm text-muted-foreground">
            Página {pagination.page} de {pagination.total_pages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              aria-label="Página anterior"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Próxima página"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => onPageChange(pagination.page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
